<template>
  <header class="header" :class="{ 'header--sticky': sticky }">
    <div class="header__left">
      <slot name="title">
        <h1 class="header__title">
          <BreadcrumbItem :value="main">{{ title }}</BreadcrumbItem>
        </h1>
      </slot>
      <Breadcrumb v-if="breadcrumb" class="header__breadcrumb" :value="breadcrumb" />
    </div>
    <div class="header__center">
      <slot name="center" />
    </div>
    <div class="header__right">
      <slot name="right">
        <HeaderItem v-if="items.length" :items="items" class="header__items" />
      </slot>

      <Portal :to="emptyStatePortalName" :disabled="!usePortal">
        <div class="row g-1 wrap">
          <slot name="actions" />
        </div>
      </Portal>
    </div>
  </header>
</template>

<script>
import { Portal, Wormhole } from 'portal-vue'
import Breadcrumb from '@/components/Breadcrumb'
import BreadcrumbItem from '@/components/Breadcrumb/Item'
import HeaderItem from './HeaderItem'

export default {
  props: {
    title: String,
    breadcrumb: Array,
    items: {
      type: Array,
      default: () => [],
    },
    sticky: {
      type: Boolean,
      default: true,
    },
    emptyStatePortalName: {
      type: String,
      default: 'empty-state-actions',
    },
  },
  data() {
    return {
      observer: null,
    }
  },
  computed: {
    main() {
      return (this.breadcrumb || []).find(b => b.main)
    },
    usePortal() {
      return Wormhole.hasTarget(this.emptyStatePortalName)
    },
  },
  methods: {
    onElementObserved([e]) {
      e.target.classList.toggle('header--top', e.intersectionRatio < 1)
    },
  },
  mounted() {
    this.observer.observe(this.$el)
    document.documentElement.style.setProperty(
      '--header-height',
      Math.floor(this.$el.getBoundingClientRect().height) + 'px',
    )
  },
  created() {
    this.observer = new IntersectionObserver(this.onElementObserved, { root: this.$el, threshold: 1 })
  },
  beforeDestroy() {
    this.observer.disconnect()
    document.documentElement.style.removeProperty('--header-height')
  },
  components: {
    Breadcrumb,
    BreadcrumbItem,
    Portal,
    HeaderItem,
  },
}
</script>

<style lang="scss">
.header {
  --transition: 150ms ease;

  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-bottom: calc(var(--container-outer-spacing) / 2);
  padding-block: calc(var(--container-outer-spacing) / 2);
  background-color: $background-grey;

  animation: fadeIn 300ms ease;

  &--sticky {
    position: sticky;
    top: -1px;
    z-index: get-layer('header');
    border-bottom: 1px solid transparent;
    transition: border-color var(--transition);

    &.header--top {
      border-color: var(--border-color);
    }
  }

  &__items {
    display: flex;
    gap: 0.5rem;
  }

  &__left {
    color: css-lighten('dark', 35%);
  }

  &__right {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    flex-grow: 1;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 300;
    margin: 0;
    line-height: 1;

    .breadcrumb__item {
      border-bottom: none;
    }
  }

  &__breadcrumb {
    margin-top: 0.5rem;
    position: relative;
    left: 2px;
  }
}
</style>
